import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Headset,
  User,
  Clock,
  MapPin,
  Wrench,
  AlertTriangle,
  UserCheck,
  ChevronRight
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const PRIORITY_OPTIONS = [
  { id: "P1", name: "P1 - Safety / Healthcare", color: "bg-red-100 text-red-700" },
  { id: "P2", name: "P2 - Urgent", color: "bg-orange-100 text-orange-700" },
  { id: "P3", name: "P3 - Not Urgent", color: "bg-yellow-100 text-yellow-700" },
  { id: "P4", name: "P4 - Can Wait", color: "bg-gray-100 text-gray-700" },
];

export default function HelpDeskQueue() {
  const { unitId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [assignment, setAssignment] = useState({
    priority: "",
    assignee_id: "",
    assignee_type: "technician",
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [unitId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ticketsRes, techRes, vendorRes] = await Promise.all([
        api.get(`/tickets?unit_id=${unitId}&status=Escalated to Help Desk`),
        api.get(`/users/technicians?unit_id=${unitId}`),
        api.get("/users/vendors")
      ]);
      setTickets(ticketsRes.data.tickets);
      setTechnicians(techRes.data);
      setVendors(vendorRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const openAssignDialog = (ticket) => {
    setSelectedTicket(ticket);
    setAssignment({
      priority: ticket.priority || "",
      assignee_id: "",
      assignee_type: "technician",
      notes: ""
    });
    setAssignDialogOpen(true);
  };

  const handleAssign = async () => {
    if (!assignment.priority || !assignment.assignee_id) {
      toast.error("Please select priority and assignee");
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/tickets/${selectedTicket.id}/assign`, assignment);
      toast.success("Ticket assigned successfully");
      setAssignDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to assign ticket");
    } finally {
      setSubmitting(false);
    }
  };

  const assigneeOptions = assignment.assignee_type === "technician" ? technicians : vendors;

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar unitId={unitId} />
      
      <main className="flex-1 p-4 md:p-8 overflow-auto">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Headset className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">Help Desk Queue</h1>
              <p className="text-gray-600">Escalated tickets awaiting assignment</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : tickets.length > 0 ? (
          <div className="space-y-4">
            {tickets.map((ticket) => (
              <Card key={ticket.id} className="bg-white border-gray-200 hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <button
                          onClick={() => navigate(`/ticket/${ticket.id}`)}
                          className="text-lg font-semibold text-indigo-600 hover:underline"
                          data-testid={`ticket-link-${ticket.id}`}
                        >
                          {ticket.job_number}
                        </button>
                        <Badge className="bg-blue-100 text-blue-700">Escalated</Badge>
                      </div>

                      <p className="text-gray-700 mb-4 line-clamp-2">{ticket.description}</p>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin className="w-4 h-4" />
                          <span>{ticket.restaurant_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Wrench className="w-4 h-4" />
                          <span>{ticket.equipment_category}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <User className="w-4 h-4" />
                          <span>{ticket.created_by_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span>{formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}</span>
                        </div>
                      </div>

                      {ticket.troubleshooting && (
                        <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                            <div className="text-sm text-amber-800">
                              <p className="font-medium">Troubleshooting attempted:</p>
                              <p>{ticket.troubleshooting.items?.filter(i => i.status === "attempted").length || 0} steps completed</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <Button
                      onClick={() => openAssignDialog(ticket)}
                      className="bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap"
                      data-testid={`assign-btn-${ticket.id}`}
                    >
                      <UserCheck className="w-4 h-4 mr-2" />
                      Assign Ticket
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-white border-gray-200">
            <CardContent className="py-12 text-center">
              <Headset className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Queue is Empty</h3>
              <p className="text-gray-500">No tickets waiting for assignment</p>
            </CardContent>
          </Card>
        )}

        {/* Assign Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Assign Ticket {selectedTicket?.job_number}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Priority *</Label>
                <Select
                  value={assignment.priority}
                  onValueChange={(v) => setAssignment(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger data-testid="priority-select">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        <div className="flex items-center gap-2">
                          <Badge className={p.color}>{p.id}</Badge>
                          <span>{p.name.split(" - ")[1]}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Assignee Type *</Label>
                <Select
                  value={assignment.assignee_type}
                  onValueChange={(v) => setAssignment(prev => ({ ...prev, assignee_type: v, assignee_id: "" }))}
                >
                  <SelectTrigger data-testid="assignee-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technician">Internal Technician</SelectItem>
                    <SelectItem value="vendor">External Vendor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Assign To *</Label>
                <Select
                  value={assignment.assignee_id}
                  onValueChange={(v) => setAssignment(prev => ({ ...prev, assignee_id: v }))}
                >
                  <SelectTrigger data-testid="assignee-select">
                    <SelectValue placeholder="Select assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    {assigneeOptions.map(a => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.full_name} ({a.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {assigneeOptions.length === 0 && (
                  <p className="text-sm text-amber-600">No {assignment.assignee_type}s available</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  placeholder="Add notes for the assignee..."
                  value={assignment.notes}
                  onChange={(e) => setAssignment(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  data-testid="assignment-notes"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAssign}
                disabled={submitting}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="confirm-assign-btn"
              >
                {submitting ? "Assigning..." : "Assign Ticket"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
